/**
 * Thin wrapper around the Usercentrics Web CMP v3 browser API (window.__ucCmp).
 *
 * The CMP script is injected in public/index.html; this module is the only place
 * that should touch __ucCmp or parse ucData from localStorage.
 *
 * API reference: https://usercentrics.com/docs/web/features/api/control-functionality/
 *
 * Consumers:
 *   UsercentricsProvider — readConsentMap on init / consent events
 *   useUsercentricsActions — acceptServiceConsent, openConsentSettings (YouTube placeholder, Legal page)
 */
import type { UcServiceData, UcWindow } from './usercentricsTypes';

const getUcWindow = (): UcWindow | undefined => {
  if (typeof window === 'undefined') return undefined;
  return window as UcWindow;
};

/** Live CMP API handle — undefined until the loader finishes booting. */
// eslint-disable-next-line no-underscore-dangle
export const getUcCmp = (): UcWindow['__ucCmp'] | undefined => getUcWindow()?.__ucCmp;

/** True when the loader <script id="usercentrics-cmp"> is in index.html (paired with USERCENTRICS_ENABLE). */
export const hasUsercentricsLoader = (): boolean => {
  if (typeof document === 'undefined') return false;
  return Boolean(document.getElementById('usercentrics-cmp'));
};

/**
 * Normalizes Usercentrics service payloads to a boolean.
 * CMP responses have varied shapes across versions — we treat any "consented" signal as true.
 */
export const isServiceConsented = (service: UcServiceData | undefined): boolean => {
  if (!service) return false;

  if (service.consent?.given === true) return true;

  if (service.status === true) return true;

  if (typeof service.status === 'object' && service.status?.consented === true) {
    return true;
  }

  return false;
};

/**
 * Fallback when __ucCmp is unavailable (slow load, ad blocker, timeout path).
 * Usercentrics persists decisions under localStorage key "ucData".
 */
export const getConsentsFromLocalStorage = (): Record<string, UcServiceData> => {
  try {
    const data = localStorage.getItem('ucData');
    if (!data) return {};

    const parsed = JSON.parse(data) as { consent?: { services?: Record<string, UcServiceData> } };
    return parsed.consent?.services ?? {};
  } catch {
    return {};
  }
};

/**
 * Primary read path for UsercentricsProvider.
 * Returns templateId → granted map used by useServiceConsent / TrackingInitializer.
 */
export const readConsentMap = async (): Promise<Record<string, boolean>> => {
  const cmp = getUcCmp();

  if (cmp?.getConsentDetails) {
    try {
      const details = await cmp.getConsentDetails();
      return Object.entries(details.services ?? {}).reduce<Record<string, boolean>>(
        (acc, [serviceId, service]) => {
          acc[serviceId] = isServiceConsented(service);
          return acc;
        },
        {}
      );
    } catch {
      // Fall through to localStorage.
    }
  }

  return Object.entries(getConsentsFromLocalStorage()).reduce<Record<string, boolean>>(
    (acc, [serviceId, service]) => {
      acc[serviceId] = isServiceConsented(service);
      return acc;
    },
    {}
  );
};

/** Opt in to a single service programmatically (e.g. YouTube placeholder "Accept" button). */
export const acceptServiceConsent = async (serviceId: string): Promise<void> => {
  const cmp = getUcCmp();
  if (!cmp) return;

  await cmp.updateServicesConsents([{ id: serviceId, consent: true }]);
  await cmp.saveConsents();
};

/** Opens CMP second layer — optionally scrolled to a specific service (Cookie settings / YouTube). */
export const openConsentSettings = async (serviceId?: string): Promise<void> => {
  const cmp = getUcCmp();
  if (!cmp) return;

  if (serviceId) {
    await cmp.showSecondLayer(serviceId);
    return;
  }

  await cmp.showSecondLayer();
};
